import * as Yup from "yup";
import Swal from "sweetalert2";

const getOrdinal = (index) => {
  const ordinals = ["First","Second","Third","Fourth","Fifth","Sixth","Seventh","Eighth","Ninth","Tenth"];
  return ordinals[index] || `${index + 1}th`;
};

const camel2Words = (str) =>
  str.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).replace(/_/g, " ").trim();

const prettifyPath = (path) => {
  if (!path) return "Field";
  const SKIP = new Set(["notification", "investigation", "investigationDoneBy", "signature"]);
  const parts = path.split(/[\[\].]/).filter(Boolean);
  const labels = [];
  for (const part of parts) {
    if (!isNaN(Number(part))) {
      const ordinal = getOrdinal(parseInt(part, 10));
      const parent = labels.pop() || "Item";
      const singular = parent.replace(/s$/, "");
      labels.push(`${ordinal} ${singular}`);
    } else if (!SKIP.has(part)) {
      labels.push(camel2Words(part));
    }
  }
  return labels.join(" › ") || "Field";
};

const prettifyPublicPath = (path) => {
  if (!path) return "Field";
  const FIELD_LABELS = {
    "firstName": "First Name",
    "middleName": "Middle Name",
    "lastName": "Last Name",
    "email": "Email Address",
    "phoneNumbers": "Phone Number",
    "unitNo": "Unit No.",
    "homeAddress": "Home Address",
    "notificationToPropertyManagerDateTime": "Notification Date & Time",
    "statement": "Statement of Incident",
    "signature": "Signature",
    "type": "Person Type",
    "specifyType": "Person Type (Specify)",
    "witnessChoice": "Witnesses",
    "attachmentChoice": "Attachments",
  };

  const parts = path.split(/[\[\].]/).filter(Boolean);
  for (const part of parts) {
    if (FIELD_LABELS[part]) return FIELD_LABELS[part];
  }
  return prettifyPath(path);
};

const humanizeMessage = (message = "") => {
  if (!message) return "This field is required.";
  if (/must be a `date` type/.test(message)) return "Please enter a valid date and time.";
  if (/must be a `number` type/.test(message)) return "Please enter a valid number.";
  if (/cannot be null/.test(message)) return "This field cannot be empty.";
  if (/is a required field/.test(message)) return "This field is required.";
  if (/must be a valid email/.test(message)) return "Please enter a valid email address.";
  if (/must be one of/.test(message)) return "Please select a valid option.";
  if (/must be a `boolean`/.test(message)) return "Please select Yes or No.";
  // Custom messages for choice fields
  if (/witness choice/i.test(message)) return message;
  if (/attachment choice/i.test(message)) return message;
  return message.charAt(0).toUpperCase() + message.slice(1);
};

const GROUP_META = {
  "Notification":        { icon: "📋", gradient: "linear-gradient(135deg,#4338ca,#6366f1)", bg: "#f5f3ff", border: "#c4b5fd", chip: "#ede9fe", chipText: "#4338ca" },
  "Persons Involved":    { icon: "👤", gradient: "linear-gradient(135deg,#0891b2,#06b6d4)", bg: "#ecfeff", border: "#a5f3fc", chip: "#cffafe", chipText: "#0e7490" },
  "Incident Details":    { icon: "📅", gradient: "linear-gradient(135deg,#7c3aed,#a78bfa)", bg: "#faf5ff", border: "#d8b4fe", chip: "#ede9fe", chipText: "#6d28d9" },
  "Witnesses":           { icon: "👀", gradient: "linear-gradient(135deg,#0284c7,#38bdf8)", bg: "#f0f9ff", border: "#bae6fd", chip: "#e0f2fe", chipText: "#0369a1" },
  "Investigation":       { icon: "🔍", gradient: "linear-gradient(135deg,#059669,#10b981)", bg: "#ecfdf5", border: "#6ee7b7", chip: "#d1fae5", chipText: "#047857" },
  "Notification Emails": { icon: "✉️", gradient: "linear-gradient(135deg,#0f766e,#14b8a6)", bg: "#f0fdfa", border: "#5eead4", chip: "#ccfbf1", chipText: "#0f766e" },
  "Other":               { icon: "⚠️", gradient: "linear-gradient(135deg,#b45309,#f59e0b)", bg: "#fffbeb", border: "#fcd34d", chip: "#fef3c7", chipText: "#92400e" },
};

const GROUP_KEYS = Object.keys(GROUP_META);

const groupErrors = (errors) => {
  const groups = Object.fromEntries(GROUP_KEYS.map((k) => [k, []]));
  for (const { path, message } of errors) {
    const item = { label: prettifyPath(path), message };
    if (path.startsWith("notification.personsInvolved")) groups["Persons Involved"].push(item);
    else if (path.startsWith("notification.manager")) groups["Notification"].push(item);
    else if (path.startsWith("investigation")) groups["Investigation"].push(item);
    else if (path.startsWith("notificationEmails")) groups["Notification Emails"].push(item);
    else groups["Other"].push(item);
  }
  return groups;
};

const groupPublicErrors = (errors) => {
  const YOUR_INFO_FIELDS = new Set(["firstName", "middleName", "lastName", "email", "phoneNumbers", "unitNo", "homeAddress", "type", "specifyType"]);

  const groups = {
    "Your Information": [],
    "Statement of Incident": [],
  };

  for (const { path, message } of errors) {
    const parts = path.split(/[\[\].]/).filter(Boolean);
    const fieldKey = parts[parts.length - 1];
    const parentKey = parts[parts.length - 2];

    const label = prettifyPublicPath(path);

    if (YOUR_INFO_FIELDS.has(fieldKey) || YOUR_INFO_FIELDS.has(parentKey)) {
      groups["Your Information"].push({ label, message });
    } else {
      groups["Statement of Incident"].push({ label, message });
    }
  }

  return groups;
};

const PUBLIC_GROUP_META = {
  "Your Information":       { icon: "👤", gradient: "linear-gradient(135deg,#163b5f,#3a6b98)", bg: "#eef3f7", border: "#c2d5e3", chip: "#dbe6f0", chipText: "#163b5f" },
  "Statement of Incident":  { icon: "📝", gradient: "linear-gradient(135deg,#0891b2,#06b6d4)", bg: "#ecfeff", border: "#a5f3fc", chip: "#cffafe", chipText: "#0e7490" },
};

const showValidationErrors = async (groups, totalCount) => {
  const active = GROUP_KEYS.filter((k) => groups[k]?.length);

  const chipsHtml = active.map((key) => {
    const m = GROUP_META[key];
    return `
      <div style="
        display:inline-flex;align-items:center;gap:6px;
        padding:5px 13px 5px 8px;border-radius:99px;
        background:${m.chip};border:1.5px solid ${m.border};
        font-size:12px;font-weight:700;color:${m.chipText};
        white-space:nowrap;
      ">
        <span style="
          display:flex;align-items:center;justify-content:center;
          width:22px;height:22px;border-radius:50%;
          background:${m.gradient};font-size:11px;
          box-shadow:0 2px 6px rgba(0,0,0,0.15);
        ">${m.icon}</span>
        ${key}
        <span style="
          background:${m.chipText};color:#fff;
          border-radius:99px;padding:1px 8px;
          font-size:10.5px;font-weight:800;
          margin-left:2px;
        ">${groups[key].length}</span>
      </div>`;
  }).join('');

  const cardsHtml = active.map((key) => {
    const m = GROUP_META[key];
    const items = groups[key];
    const shown = items.slice(0, 5);
    const extra = items.length - 5;

    const rowsHtml = shown.map((item, i) => `
      <div style="
        display:flex;align-items:flex-start;gap:12px;
        background:#fff;border-radius:10px;
        padding:11px 14px;
        border:1.5px solid #eef2ff;
        box-shadow:0 2px 8px rgba(99,102,241,0.04);
        margin-bottom:8px;
        transition:all 0.15s;
      ">
        <div style="
          flex-shrink:0;width:24px;height:24px;border-radius:8px;
          background:linear-gradient(135deg,#ef4444,#f87171);
          color:#fff;font-size:10.5px;font-weight:800;
          display:flex;align-items:center;justify-content:center;
          margin-top:1px;box-shadow:0 2px 6px rgba(239,68,68,0.3);
        ">${i + 1}</div>
        <div style="flex:1;min-width:0;">
          <div style="font-size:13px;font-weight:700;color:#1e1b4b;line-height:1.4;margin-bottom:3px;">${item.label}</div>
          <div style="font-size:12px;color:#ef4444;font-weight:600;display:flex;align-items:center;gap:5px;">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            ${humanizeMessage(item.message)}
          </div>
        </div>
      </div>`).join('');

    const overflowHtml = extra > 0
      ? `<div style="text-align:center;font-size:12px;color:#94a3b8;font-style:italic;padding:4px 0 8px;font-weight:500;">+ ${extra} more field${extra !== 1 ? 's' : ''} in this section</div>`
      : '';

    return `
      <div style="
        border-radius:14px;overflow:hidden;margin-bottom:14px;
        border:1.5px solid ${m.border};
        box-shadow:0 4px 16px rgba(99,102,241,0.06);
      ">
        <div style="
          background:${m.gradient};
          display:flex;align-items:center;gap:10px;
          padding:12px 18px;
          position:relative;overflow:hidden;
        ">
          <div style="
            position:absolute;top:-20px;right:-10px;
            width:80px;height:80px;border-radius:50%;
            background:rgba(255,255,255,0.08);
            pointer-events:none;
          "></div>
          <span style="
            width:32px;height:32px;background:rgba(255,255,255,0.18);
            border-radius:10px;display:flex;align-items:center;
            justify-content:center;font-size:16px;flex-shrink:0;
            backdrop-filter:blur(4px);border:1px solid rgba(255,255,255,0.2);
            position:relative;z-index:1;
          ">${m.icon}</span>
          <span style="font-size:14px;font-weight:800;color:#fff;position:relative;z-index:1;letter-spacing:-0.2px;">${key}</span>
          <span style="
            margin-left:auto;
            background:rgba(255,255,255,0.2);
            border:1px solid rgba(255,255,255,0.3);
            color:#fff;font-size:11.5px;font-weight:700;
            padding:3px 12px;border-radius:99px;
            white-space:nowrap;position:relative;z-index:1;
          ">${items.length} issue${items.length !== 1 ? 's' : ''}</span>
        </div>
        <div style="padding:12px 14px 6px;background:${m.bg};">
          ${rowsHtml}${overflowHtml}
        </div>
      </div>`;
  }).join('');

  const html = `
    <style>
      .ver-scroll {
        font-family:'DM Sans','Inter',-apple-system,sans-serif;
        color:#1e1b4b;text-align:left;
        max-height:58vh;overflow-y:auto;
        padding-right:6px;
        scrollbar-width:thin;
        scrollbar-color:#c4b5fd transparent;
      }
      .ver-scroll::-webkit-scrollbar { width:5px; }
      .ver-scroll::-webkit-scrollbar-track { background:transparent; }
      .ver-scroll::-webkit-scrollbar-thumb { background:#c4b5fd;border-radius:99px; }
    </style>
    <div class="ver-scroll">
      <div style="
        background:linear-gradient(135deg,#0f0c29 0%,#1e1b4b 40%,#312e81 75%,#4338ca 100%);
        border-radius:16px;padding:20px 22px;margin-bottom:20px;
        display:flex;align-items:center;gap:16px;
        box-shadow:0 10px 30px rgba(30,27,75,0.3);
        position:relative;overflow:hidden;
      ">
        <div style="
          position:absolute;top:-30px;right:-20px;
          width:140px;height:140px;border-radius:50%;
          background:radial-gradient(circle,rgba(99,102,241,0.25),transparent 70%);
          pointer-events:none;
        "></div>
        <div style="
          width:54px;height:54px;
          background:linear-gradient(135deg,rgba(255,255,255,0.15),rgba(255,255,255,0.05));
          border:1.5px solid rgba(255,255,255,0.2);
          border-radius:15px;display:flex;align-items:center;justify-content:center;
          font-size:26px;flex-shrink:0;backdrop-filter:blur(8px);
          position:relative;z-index:1;
          box-shadow:0 4px 16px rgba(0,0,0,0.2);
        ">📝</div>
        <div style="flex:1;min-width:0;position:relative;z-index:1;">
          <p style="margin:0;font-size:16px;font-weight:800;color:#fff;letter-spacing:-0.3px;line-height:1.3;">
            Almost there — a few fields need attention
          </p>
          <p style="margin:4px 0 0;font-size:12.5px;color:rgba(255,255,255,0.65);">
            Please fix the highlighted fields and resubmit.
          </p>
        </div>
        <div style="
          background:linear-gradient(135deg,#ef4444,#f87171);
          color:#fff;font-size:13.5px;font-weight:800;
          padding:8px 16px;border-radius:12px;
          white-space:nowrap;flex-shrink:0;
          position:relative;z-index:1;
          box-shadow:0 4px 12px rgba(239,68,68,0.35);
        ">${totalCount} Issue${totalCount !== 1 ? 's' : ''}</div>
      </div>

      <div style="display:flex;flex-wrap:wrap;gap:7px;margin-bottom:20px;">
        ${chipsHtml}
      </div>

      ${cardsHtml}

      <div style="
        background:linear-gradient(135deg,#f5f3ff,#ede9fe);
        border-radius:12px;border:1.5px solid #c4b5fd;
        padding:12px 16px;
        display:flex;align-items:flex-start;gap:10px;
        font-size:12.5px;color:#4338ca;
        margin-top:6px;margin-bottom:8px;
        font-weight:500;
      ">
        <span style="font-size:16px;flex-shrink:0;">💡</span>
        <span>Fields marked <strong style="color:#4338ca;">*</strong> are required. Empty date or time pickers also count as missing values.</span>
      </div>
    </div>`;

  await Swal.fire({
    html,
    confirmButtonText: '✏️  Fix Issues',
    confirmButtonColor: '#4338ca',
    showCancelButton: true,
    cancelButtonText: 'Dismiss',
    cancelButtonColor: '#94a3b8',
    width: 700,
    padding: '24px',
    background: '#fafbff',
    customClass: {
      confirmButton: 'swal-pir-confirm',
      cancelButton: 'swal-pir-cancel',
      popup: 'swal-pir-popup',
    },
  });
};

const showPublicValidationErrors = async (groups, totalCount) => {
  const activeKeys = Object.keys(groups).filter((k) => groups[k]?.length);

  const chipsHtml = activeKeys.map((key) => {
    const m = PUBLIC_GROUP_META[key];
    return `
      <div style="
        display:inline-flex;align-items:center;gap:6px;
        padding:5px 13px 5px 8px;border-radius:99px;
        background:${m.chip};border:1.5px solid ${m.border};
        font-size:12px;font-weight:700;color:${m.chipText};
        white-space:nowrap;
      ">
        <span style="
          display:flex;align-items:center;justify-content:center;
          width:22px;height:22px;border-radius:50%;
          background:${m.gradient};font-size:11px;
          box-shadow:0 2px 6px rgba(0,0,0,0.15);
        ">${m.icon}</span>
        ${key}
        <span style="
          background:${m.chipText};color:#fff;
          border-radius:99px;padding:1px 8px;
          font-size:10.5px;font-weight:800;
          margin-left:2px;
        ">${groups[key].length}</span>
      </div>`;
  }).join('');

  const cardsHtml = activeKeys.map((key) => {
    const m = PUBLIC_GROUP_META[key];
    const items = groups[key];
    const shown = items.slice(0, 5);
    const extra = items.length - 5;

    const rowsHtml = shown.map((item, i) => `
      <div style="
        display:flex;align-items:flex-start;gap:12px;
        background:#fff;border-radius:10px;
        padding:11px 14px;
        border:1.5px solid ${m.border};
        margin-bottom:8px;
      ">
        <div style="
          flex-shrink:0;width:24px;height:24px;border-radius:8px;
          background:linear-gradient(135deg,#ef4444,#f87171);
          color:#fff;font-size:10.5px;font-weight:800;
          display:flex;align-items:center;justify-content:center;
          margin-top:1px;box-shadow:0 2px 6px rgba(239,68,68,0.3);
        ">${i + 1}</div>
        <div style="flex:1;min-width:0;">
          <div style="font-size:13px;font-weight:700;color:#1e293b;line-height:1.4;margin-bottom:3px;">${item.label}</div>
          <div style="font-size:12px;color:#ef4444;font-weight:600;display:flex;align-items:center;gap:5px;">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            ${humanizeMessage(item.message)}
          </div>
        </div>
      </div>`).join('');

    const overflowHtml = extra > 0
      ? `<div style="text-align:center;font-size:12px;color:#94a3b8;font-style:italic;padding:4px 0 8px;font-weight:500;">+ ${extra} more field${extra !== 1 ? 's' : ''}</div>`
      : '';

    return `
      <div style="
        border-radius:14px;overflow:hidden;margin-bottom:14px;
        border:1.5px solid ${m.border};
      ">
        <div style="
          background:${m.gradient};
          display:flex;align-items:center;gap:10px;
          padding:12px 18px;
        ">
          <span style="
            width:32px;height:32px;background:rgba(255,255,255,0.18);
            border-radius:10px;display:flex;align-items:center;
            justify-content:center;font-size:16px;flex-shrink:0;
            border:1px solid rgba(255,255,255,0.2);
          ">${m.icon}</span>
          <span style="font-size:14px;font-weight:800;color:#fff;letter-spacing:-0.2px;">${key}</span>
          <span style="
            margin-left:auto;
            background:rgba(255,255,255,0.2);
            border:1px solid rgba(255,255,255,0.3);
            color:#fff;font-size:11.5px;font-weight:700;
            padding:3px 12px;border-radius:99px;
            white-space:nowrap;
          ">${items.length} issue${items.length !== 1 ? 's' : ''}</span>
        </div>
        <div style="padding:12px 14px 6px;background:${m.bg};">
          ${rowsHtml}${overflowHtml}
        </div>
      </div>`;
  }).join('');

  const html = `
    <style>
      .pub-scroll {
        font-family:'DM Sans','Inter',-apple-system,sans-serif;
        color:#1e293b;text-align:left;
        max-height:58vh;overflow-y:auto;
        padding-right:6px;
        scrollbar-width:thin;
        scrollbar-color:#c2d5e3 transparent;
      }
      .pub-scroll::-webkit-scrollbar { width:5px; }
      .pub-scroll::-webkit-scrollbar-track { background:transparent; }
      .pub-scroll::-webkit-scrollbar-thumb { background:#c2d5e3;border-radius:99px; }
    </style>
    <div class="pub-scroll">
      <div style="
        background:linear-gradient(135deg,#061524 0%,#163b5f 60%,#3a6b98 100%);
        border-radius:16px;padding:20px 22px;margin-bottom:20px;
        display:flex;align-items:center;gap:16px;
        box-shadow:0 10px 30px rgba(6,21,36,0.3);
        position:relative;overflow:hidden;
      ">
        <div style="
          position:absolute;top:-30px;right:-20px;
          width:140px;height:140px;border-radius:50%;
          background:radial-gradient(circle,rgba(58,107,152,0.3),transparent 70%);
          pointer-events:none;
        "></div>
        <div style="
          width:54px;height:54px;
          background:rgba(255,255,255,0.15);
          border:1.5px solid rgba(255,255,255,0.2);
          border-radius:15px;display:flex;align-items:center;justify-content:center;
          font-size:26px;flex-shrink:0;
          position:relative;z-index:1;
        ">📝</div>
        <div style="flex:1;min-width:0;position:relative;z-index:1;">
          <p style="margin:0;font-size:16px;font-weight:800;color:#fff;letter-spacing:-0.3px;line-height:1.3;">
            A few fields need your attention
          </p>
          <p style="margin:4px 0 0;font-size:12.5px;color:rgba(255,255,255,0.65);">
            Please fix the highlighted fields and resubmit.
          </p>
        </div>
        <div style="
          background:linear-gradient(135deg,#ef4444,#f87171);
          color:#fff;font-size:13.5px;font-weight:800;
          padding:8px 16px;border-radius:12px;
          white-space:nowrap;flex-shrink:0;
          position:relative;z-index:1;
          box-shadow:0 4px 12px rgba(239,68,68,0.35);
        ">${totalCount} Issue${totalCount !== 1 ? 's' : ''}</div>
      </div>

      <div style="display:flex;flex-wrap:wrap;gap:7px;margin-bottom:20px;">
        ${chipsHtml}
      </div>

      ${cardsHtml}

      <div style="
        background:#eef3f7;
        border-radius:12px;border:1.5px solid #c2d5e3;
        padding:12px 16px;
        display:flex;align-items:flex-start;gap:10px;
        font-size:12.5px;color:#163b5f;
        margin-top:6px;margin-bottom:8px;
        font-weight:500;
      ">
        <span style="font-size:16px;flex-shrink:0;">💡</span>
        <span>Fields marked <strong style="color:#ef4444;">*</strong> are required. Make sure to answer the witnesses and attachments questions, and add your signature before submitting.</span>
      </div>
    </div>`;

  await Swal.fire({
    html,
    confirmButtonText: '✏️  Fix Issues',
    confirmButtonColor: '#163b5f',
    showCancelButton: true,
    cancelButtonText: 'Dismiss',
    cancelButtonColor: '#94a3b8',
    width: 660,
    padding: '24px',
    background: '#f8fafc',
  });
};

const witnessShape = Yup.object().shape({
  firstName: Yup.string().trim().required("First name is required"),
  middleName: Yup.string().trim(),
  lastName: Yup.string().trim().required("Last name is required"),
  address: Yup.string().trim(),
  phoneNo: Yup.string().trim(),
  email: Yup.string().trim().email("Invalid email"),
});

const signatureShape = Yup.object().shape({
  signature: Yup.string().required("Signature is required"),
  date: Yup.date().nullable().required("Signature date is required"),
});

const personShape = Yup.object().shape({
  type: Yup.string()
    .oneOf(["employee", "tenant", "contractor", "other"])
    .required("Person type is required"),
  specifyType: Yup.string().when("type", {
    is: "other",
    then: (s) => s.required("Please specify person type"),
    otherwise: (s) => s,
  }),
  firstName: Yup.string().trim().required("First name is required"),
  middleName: Yup.string().trim(),
  lastName: Yup.string().trim().required("Last name is required"),
  email: Yup.string().trim().email("Invalid email"),
  phoneNumbers: Yup.array().of(Yup.string().trim()),
  unitNo: Yup.string().trim(),
  homeAddress: Yup.string().trim(),
  notificationToPropertyManagerDateTime: Yup.date()
    .nullable()
    .typeError("Please enter a valid date and time."),
  statement: Yup.string().trim(),
  attachments: Yup.array().of(Yup.string()),
  // witnessChoice and attachmentChoice are not required here (internal schema only)
  witnesses: Yup.array().of(witnessShape),
  signature: signatureShape,
});

export const IncidentReportValidationSchema = Yup.object().shape({
  notification: Yup.object().shape({
    managerReporting: Yup.string().trim().required("Manager name is required"),
    managerEmail: Yup.string().trim().email("Invalid email"),
    managerPhoneNo: Yup.string().trim(),
    personsInvolved: Yup.array()
      .of(personShape)
      .min(1, "At least one person must be involved"),
  }),
  investigation: Yup.object().shape({
    occurrenceDateTime: Yup.date().nullable().typeError("Please enter a valid date and time.").required("Occurrence date/time is required"),
    notificationToPropertyManagerDateTime: Yup.date().nullable().typeError("Please enter a valid date and time.").required("Notification date/time is required"),
    weatherConditions: Yup.array().of(Yup.string()).min(1, "At least one weather condition is required"),
    weatherConditionsOther: Yup.string().trim(),
    temperatureCelsius: Yup.number().nullable().min(-100, "Min -100").max(100, "Max 100").required("Temperature is required"),
    environmentConditions: Yup.array().of(Yup.string()).min(1, "At least one environment condition is required"),
    visibility: Yup.array().of(Yup.string()).min(1, "At least one visibility condition is required"),
    statement: Yup.string().trim().required("Statement is required"),
    witnesses: Yup.array().of(witnessShape),
    attachments: Yup.array().of(Yup.string()),
    leaseDocument: Yup.string(),
    carInsuranceDocument: Yup.string(),
    homeInsuranceDocument: Yup.string(),
    investigationDoneBy: Yup.object().shape({
      name: Yup.string().trim().required("Investigator name is required"),
      date: Yup.date().nullable().required("Investigation date is required"),
    }),
  }),
  notificationEmails: Yup.array().of(
    Yup.object().shape({
      firstName: Yup.string().trim().required("First name is required"),
      lastName: Yup.string().trim().required("Last name is required"),
      email: Yup.string().trim().email("Invalid email").required("Email is required"),
      managersNote: Yup.string().trim(),
    })
  ),
  followUpRequired: Yup.boolean().nullable().required("Please indicate if a follow-up is required"),
});

/**
 * Public schema — validates the person's self-submitted incident report.
 *
 * Key additions vs original:
 *  • witnessChoice must be 'yes' or 'no' (cannot be null/undefined)
 *  • attachmentChoice must be 'yes' or 'no' (cannot be null/undefined)
 *  • If attachmentChoice === 'yes', at least one attachment is required
 *  • If witnessChoice === 'yes', at least one witness with firstName + lastName is required
 */
export const PublicIncidentReportValidationSchema = Yup.object().shape({
  notification: Yup.object().shape({
    personsInvolved: Yup.array().of(
      Yup.object().shape({
        type: Yup.string()
          .oneOf(["employee", "tenant", "contractor", "other"])
          .required("Person type is required"),
        specifyType: Yup.string().when("type", {
          is: "other",
          then: (s) => s.required("Please specify person type"),
        }),
        firstName: Yup.string().trim().required("First name is required"),
        middleName: Yup.string().trim(),
        lastName: Yup.string().trim().required("Last name is required"),
        email: Yup.string().trim().email("Invalid email").required("Email is required"),
        phoneNumbers: Yup.array()
          .of(Yup.string().trim().required("Phone number is required"))
          .min(1, "Phone number is required"),
        unitNo: Yup.string().trim().required("Unit No. is required (enter N/A if not applicable)"),
        homeAddress: Yup.string().trim().required("Home Address is required"),
        notificationToPropertyManagerDateTime: Yup.date()
          .nullable()
          .typeError("Please enter a valid date and time.")
          .required("Notification date/time is required"),
        statement: Yup.string().trim().required("Statement is required"),

        // ── Attachment choice gate ───────────────────────────────────────
        // User MUST explicitly choose yes or no — null is not acceptable
        attachmentChoice: Yup.string()
          .oneOf(["yes", "no"], "Please indicate whether you have files to attach")
          .required("Please indicate whether you have files to attach"),

        // If they said yes, they must have uploaded at least one file
        attachments: Yup.array().when("attachmentChoice", {
          is: "yes",
          then: (s) =>
            s
              .of(Yup.string())
              .min(1, "Please upload at least one file, or change your answer to 'No Attachments'"),
          otherwise: (s) => s.of(Yup.string()),
        }),

        // ── Witness choice gate ──────────────────────────────────────────
        // User MUST explicitly choose yes or no — null is not acceptable
        witnessChoice: Yup.string()
          .oneOf(["yes", "no"], "Please indicate whether there were any witnesses")
          .required("Please indicate whether there were any witnesses"),

        // If they said yes, at least one witness with first + last name required
        witnesses: Yup.array().when("witnessChoice", {
          is: "yes",
          then: (s) =>
            s
              .of(
                Yup.object().shape({
                  firstName: Yup.string().trim().required("Witness first name is required"),
                  middleName: Yup.string().trim(),
                  lastName: Yup.string().trim().required("Witness last name is required"),
                  address: Yup.string().trim(),
                  phoneNo: Yup.string().trim(),
                  email: Yup.string().trim().email("Invalid witness email"),
                })
              )
              .min(1, "Please add at least one witness, or change your answer to 'No Witnesses'"),
          otherwise: (s) => s.of(Yup.object()),
        }),

        // ── Signature ────────────────────────────────────────────────────
        signature: Yup.object().shape({
          signature: Yup.string().required("Signature is required"),
          date: Yup.date().nullable(),
        }).required("Signature is required"),
      })
    ),
  }),
});

export const validateIncidentReport = async (values) => {
  try {
    await IncidentReportValidationSchema.validate(values, { abortEarly: false });
    return true;
  } catch (err) {
    const rawErrors = err?.inner?.map((e) => ({ path: e.path || "", message: e.message })) || [];
    if (!rawErrors.length) return false;
    const groups = groupErrors(rawErrors);
    await showValidationErrors(groups, rawErrors.length);
    return false;
  }
};

export const validatePublicIncidentReport = async (values) => {
  try {
    await PublicIncidentReportValidationSchema.validate(values, { abortEarly: false });
    return true;
  } catch (err) {
    const rawErrors = err?.inner?.map((e) => ({ path: e.path || "", message: e.message })) || [];
    if (!rawErrors.length) return false;
    const groups = groupPublicErrors(rawErrors);
    const totalCount = rawErrors.length;
    await showPublicValidationErrors(groups, totalCount);
    return false;
  }
};